/**
 * ImpersonationBanner Component
 * Shows when super admin is impersonating another user
 * Provides button to end impersonation and return to admin view
 */

import React from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import { useImpersonation } from '@/shared/contexts/ImpersonationContext'

export const ImpersonationBanner: React.FC = React.memo(function ImpersonationBanner() {
  const { t } = useTranslation('shared')
  const {
    isImpersonating,
    targetUser,
    isEnding,
    endImpersonation,
    hasExpiredNotice,
    dismissExpiredNotice,
    endFailureReason,
  } = useImpersonation()

  // The session ended by itself (1 h cap reached, or the server rejected the
  // token). The admin is already back in their OWN account — say so, out loud.
  // Returning them there in silence is how a support agent ends up reading
  // their own tenant's data believing it is the customer's.
  if (hasExpiredNotice) {
    return (
      <div
        role="alert"
        className="bg-amber-600 text-white px-4 py-3 shadow-lg z-50 [html[data-theme=light]_&]:bg-amber-700"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-semibold">{t('impersonation.expiredTitle')}</p>
            <p className="text-sm text-white/90">{t('impersonation.expiredMessage')}</p>
          </div>
          <button
            type="button"
            onClick={dismissExpiredNotice}
            className="px-4 py-1.5 bg-white text-amber-800 font-semibold rounded-lg hover:bg-gray-100 transition-colors text-sm"
          >
            {t('impersonation.expiredDismiss')}
          </button>
        </div>
      </div>
    )
  }

  // Don't render if not impersonating
  if (!isImpersonating || !targetUser) return null

  const handleEndImpersonation = async () => {
    await endImpersonation()
  }

  return (
    /*
     * ⚠️ Aquí había un `z-50` INERTE: el `z-index` no pinta nada sobre un
     * elemento `position: static`, y éste lo es. Medido en el navegador, su
     * z-index calculado era `auto`. Es decir, la intención de quedar por encima
     * estaba escrita y no se aplicaba NUNCA — y el banner terminaba debajo de la
     * barra fija (`z-30`), invisible. La solución no es subir el número: es que
     * el aviso no comparta sitio con la barra. Lo resuelve el layout, que ahora
     * lo monta dentro de la columna que ya reserva el hueco.
     */
    <div
      role="status"
      className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-3 shadow-lg"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
        {/* Info message */}
        <div className="flex items-center gap-3">
          {/* User switch icon */}
          <div className="flex-shrink-0">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 11l-4 4m0 0l4 4m-4-4h12"
              />
            </svg>
          </div>

          <div>
            <p className="font-semibold">{t('impersonation.title')}</p>
            <p className="text-sm text-white/90">
              {t('impersonation.viewingAs')} <strong>{targetUser.email}</strong>
              {targetUser.tenantName && (
                <span className="text-white/70"> - {targetUser.tenantName}</span>
              )}
            </p>
          </div>
        </div>

        {/* The server refused to close the session, so it is STILL OPEN there.
            Announced next to the button that failed, and the banner stays put:
            it is the only way back to that button. */}
        {endFailureReason !== null && (
          <div
            role="alert"
            className="w-full bg-white/15 border border-white/40 rounded-md px-3 py-2"
            data-testid="impersonation-end-failed"
          >
            <p className="text-sm">
              <strong>{t('impersonation.endFailedTitle')}</strong>{' '}
              {endFailureReason === '' ? t('impersonation.endFailedFallback') : endFailureReason}
            </p>
          </div>
        )}

        {/* Action */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleEndImpersonation}
            disabled={isEnding}
            aria-label={t('impersonation.endSession')}
            className="px-4 py-1.5 bg-white text-purple-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors text-sm disabled:opacity-50 flex items-center gap-2"
          >
            {isEnding ? (
              <>
                <div className="w-4 h-4 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
                {t('impersonation.ending')}
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                {t('impersonation.endButton')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
})
